/**
 * Mini-map — a tiny overview of the FilmStrip with a draggable
 * viewport rectangle. Only rendered when the strip's content overflows
 * its viewport; otherwise it's irrelevant overhead.
 *
 * Each frame is drawn as a thin coloured tick (cam-color) at a width
 * proportional to the frame's actual film-strip width. The viewport
 * rectangle reflects which slice of the strip is on-screen; dragging
 * the rectangle scrolls the strip.
 */
import { useRef } from "react";
import { useArrangeStore } from "../../local/arrange/arrange-store";
import {
  effectiveBarsForChunk,
  frameWidthForBars,
} from "../../local/arrange/arrange-store";
import { minimapDragScroll, MINIMAP_CLICK_SLOP_PX } from "./minimap-drag";

const MINIMAP_HEIGHT = 18;

export function MiniMap() {
  const arrangement = useArrangeStore((s) => s.arrangement);
  const chunks = useArrangeStore((s) => s.chunks);
  const cams = useArrangeStore((s) => s.cams);
  const selectedCamId = useArrangeStore((s) => s.selectedCamId);
  const view = useArrangeStore((s) => s.view);
  const currentItemId = useArrangeStore((s) => s.playback.currentItemId);
  const focusedItemId = useArrangeStore((s) => s.focusedItemId);
  const setStripScrollPx = useArrangeStore((s) => s.setStripScrollPx);
  const setStripScrubbing = useArrangeStore((s) => s.setStripScrubbing);
  const seekToItem = useArrangeStore((s) => s.seekToItem);
  const jobBpm = useArrangeStore((s) => s.jobBpm);
  const jobBeatsPerBar = useArrangeStore((s) => s.jobBeatsPerBar);

  const cam = cams.find((c) => c.id === selectedCamId) ?? cams[0] ?? null;
  const camColor = cam?.color ?? "#FF5722";

  const wrapRef = useRef<HTMLDivElement | null>(null);
  // The gesture stays ambiguous until the pointer travels past the
  // click slop: a discrete CLICK navigates (focus + seek), a DRAG only
  // scrolls the strip viewport. `scrubbing` flips once the slop is
  // exceeded — from then on the press can never seek.
  const draggingRef = useRef<{
    pointerId: number;
    startX: number;
    startScroll: number;
    scrubbing: boolean;
  } | null>(null);

  // Hide when not needed.
  const overflowing = view.stripContentWidthPx > view.stripViewportWidthPx + 2;
  if (!overflowing || arrangement.length === 0) return null;

  const chunkById = new Map(chunks.map((c) => [c.id, c]));
  // Build per-frame width array (mirrors FilmStrip math).
  const widths = arrangement.map((item) => {
    const ck = chunkById.get(item.chunkId);
    if (!ck) return 0;
    return frameWidthForBars(effectiveBarsForChunk(ck, jobBpm, jobBeatsPerBar));
  });
  const totalContent = widths.reduce((s, w) => s + w, 0) + arrangement.length * 8; // +cursors

  const minimapWidth = view.stripViewportWidthPx;
  if (minimapWidth <= 0) return null;
  const scale = minimapWidth / totalContent;

  const viewportPx = Math.max(
    24,
    (view.stripViewportWidthPx / view.stripContentWidthPx) * minimapWidth,
  );
  const viewportLeft =
    (view.stripScrollPx / Math.max(1, view.stripContentWidthPx)) * minimapWidth;

  function pointerToScroll(clientX: number): number {
    const el = wrapRef.current;
    if (!el) return view.stripScrollPx;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    // Centre the viewport on the click point.
    const desiredLeftMm = x - viewportPx / 2;
    const scrollPx =
      (desiredLeftMm / minimapWidth) * view.stripContentWidthPx;
    return Math.max(
      0,
      Math.min(
        view.stripContentWidthPx - view.stripViewportWidthPx,
        scrollPx,
      ),
    );
  }

  /** Walk the per-frame layout to figure out which arrangement item
   *  sits under `clientX`. Returns null if the click landed in the
   *  inter-frame gap or outside the strip's content. */
  function pointerToItemIndex(clientX: number): number | null {
    const el = wrapRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    let cursor = 4;
    for (let i = 0; i < arrangement.length; i++) {
      const w = widths[i];
      const start = cursor * scale;
      const end = (cursor + w) * scale;
      if (x >= start && x <= end) return i;
      cursor += w + 8;
    }
    return null;
  }

  function onPointerDown(e: React.PointerEvent) {
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    // Don't seek or scroll yet — we can't know whether this press is
    // a click (navigate) or a drag (scroll-only) until the pointer
    // either lifts or travels past the slop.
    draggingRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startScroll: view.stripScrollPx,
      scrubbing: false,
    };
  }
  function onPointerMove(e: React.PointerEvent) {
    const d = draggingRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dx = e.clientX - d.startX;
    if (!d.scrubbing) {
      if (Math.abs(dx) <= MINIMAP_CLICK_SLOP_PX) return;
      d.scrubbing = true;
      // Freeze the strip's auto-center smooth scroll while we write
      // scrollLeft directly — the two would fight over the scroller.
      setStripScrubbing(true);
    }
    setStripScrollPx(
      minimapDragScroll({
        startScrollPx: d.startScroll,
        dxPx: dx,
        minimapWidthPx: minimapWidth,
        contentWidthPx: view.stripContentWidthPx,
        viewportWidthPx: view.stripViewportWidthPx,
      }),
    );
  }
  function onPointerUp(e: React.PointerEvent) {
    const d = draggingRef.current;
    draggingRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (!d || d.pointerId !== e.pointerId) return;
    if (d.scrubbing) {
      setStripScrubbing(false);
      return;
    }
    // Discrete click. On a tick = focus + seek + tag as current
    // playback item; in a gap = just center the viewport there.
    const idx = pointerToItemIndex(e.clientX);
    if (idx !== null) {
      seekToItem(arrangement[idx].id);
    } else {
      setStripScrollPx(pointerToScroll(e.clientX));
    }
  }
  function onPointerCancel(e: React.PointerEvent) {
    const d = draggingRef.current;
    draggingRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (d?.scrubbing) setStripScrubbing(false);
  }

  // Place each frame's tick.
  let runningX = 4;
  return (
    <div
      ref={wrapRef}
      className="relative w-full mt-1 select-none cursor-pointer rounded border border-rule"
      style={{
        height: MINIMAP_HEIGHT,
        background:
          "linear-gradient(180deg, #1A1816 0%, #0E0D0B 100%)",
        // Dedicated scrub surface (like a scrollbar): the pointer owns
        // the gesture, never the browser's native pan.
        touchAction: "none",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      title="Mini-map · click to jump · drag to scroll"
    >
      {arrangement.map((item, i) => {
        const w = widths[i] * scale;
        const left = runningX * scale;
        runningX += widths[i] + 8;
        const isPlaying = item.id === currentItemId;
        const isFocused = item.id === focusedItemId;
        // Two highlight states share the lane: cobalt = focused (user
        // selected this frame in the strip), hot = currently playing.
        // Idle items render in the cam-color at half opacity.
        let bg: string;
        let opacity: number;
        if (isPlaying) {
          bg = "#FFB58A"; // hot tail
          opacity = 1;
        } else if (isFocused) {
          bg = "#FF5722"; // hot — matches the strip's focus bezel
          opacity = 1;
        } else {
          bg = camColor;
          opacity = 0.6;
        }
        return (
          <span
            key={item.id}
            aria-hidden
            className="absolute"
            style={{
              left,
              width: Math.max(1, w),
              top: 3,
              bottom: 3,
              background: bg,
              opacity,
              borderRadius: 1,
            }}
          />
        );
      })}
      {/* Viewport rectangle */}
      <span
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          left: viewportLeft,
          width: viewportPx,
          top: 0,
          bottom: 0,
          border: "1px solid rgba(255,255,255,0.65)",
          background: "rgba(255,255,255,0.06)",
          boxShadow:
            "inset 0 0 0 1px rgba(0,0,0,0.45), 0 0 6px rgba(0,0,0,0.6)",
          borderRadius: 1,
        }}
      />
    </div>
  );
}
